import { Hash, P2PKH, Utils } from '@bsv/sdk'

import type { ActionInput, ActionOutput } from './protocol'

// What a payment prompt shows besides the outputs: how much the ordinal's own inputs bring,
// and which outputs come back to the viewer. Display only: the server checks the action
// itself, so anything unreadable here just leaves the prompt without these lines.

const BEEF_V1 = 0xefbe0001
const BEEF_V2 = 0xefbe0002
const ATOMIC_BEEF = 0x01010101

/** A reader over the ordinal's bytes that refuses any count the remaining bytes can't hold,
 * so a forged count fails at once instead of spinning the page. */
class Cursor {
    pos = 0
    constructor(readonly bytes: number[]) {}
    take(length: number): number[] {
        if (!Number.isSafeInteger(length) || length < 0 || this.pos + length > this.bytes.length)
            throw new Error('truncated')
        const out = this.bytes.slice(this.pos, this.pos + length)
        this.pos += length
        return out
    }
    u8(): number {
        return this.take(1)[0]
    }
    u32(): number {
        const [a, b, c, d] = this.take(4)
        return (a | (b << 8) | (c << 16) | (d << 24)) >>> 0
    }
    u64(): number {
        const low = this.u32()
        const high = this.u32()
        const value = high * 2 ** 32 + low
        if (!Number.isSafeInteger(value)) throw new Error('amount too large')
        return value
    }
    varInt(): number {
        const first = this.u8()
        if (first < 0xfd) return first
        if (first === 0xfd) {
            const [a, b] = this.take(2)
            return a | (b << 8)
        }
        if (first === 0xfe) return this.u32()
        return this.u64()
    }
    /** A count of items each at least `minBytes` long. */
    count(minBytes: number): number {
        const n = this.varInt()
        if (n * minBytes > this.bytes.length - this.pos) throw new Error('count too large')
        return n
    }
}

function skipBump(cursor: Cursor) {
    cursor.varInt() // block height
    const levels = cursor.u8()
    if (levels > 64) throw new Error('bad merkle path')
    for (let level = 0; level < levels; level++) {
        const leaves = cursor.count(2)
        for (let i = 0; i < leaves; i++) {
            cursor.varInt() // offset
            const flags = cursor.u8()
            if (!(flags & 1)) cursor.take(32) // a hash unless it's a duplicate
        }
    }
}

/** One transaction's output values, and its txid. */
function readTransaction(cursor: Cursor): { txid: string; outputs: number[] } {
    const start = cursor.pos
    cursor.take(4) // version
    const inputs = cursor.count(41)
    for (let i = 0; i < inputs; i++) {
        cursor.take(36)
        cursor.take(cursor.varInt())
        cursor.take(4)
    }
    const outputCount = cursor.count(9)
    const outputs: number[] = []
    for (let i = 0; i < outputCount; i++) {
        outputs.push(cursor.u64())
        cursor.take(cursor.varInt())
    }
    cursor.take(4) // lock time
    const raw = cursor.bytes.slice(start, cursor.pos)
    const txid = Utils.toHex(Hash.hash256(raw).reverse())
    return { txid, outputs }
}

/** The output values of every transaction a BEEF carries, by txid, or null if it can't be
 * read. */
function beefTransactions(hex: string): Map<string, number[]> | null {
    try {
        const cursor = new Cursor(Utils.toArray(hex, 'hex'))
        let version = cursor.u32()
        if (version === ATOMIC_BEEF) {
            cursor.take(32)
            version = cursor.u32()
        }
        if (version !== BEEF_V1 && version !== BEEF_V2) return null
        const bumps = cursor.count(2)
        for (let i = 0; i < bumps; i++) skipBump(cursor)
        const count = cursor.count(10)
        const transactions = new Map<string, number[]>()
        for (let i = 0; i < count; i++) {
            if (version === BEEF_V2) {
                const format = cursor.u8()
                if (format === 2) {
                    cursor.take(32)
                    continue
                }
                if (format === 1) cursor.varInt()
            }
            const tx = readTransaction(cursor)
            if (version === BEEF_V1 && cursor.u8() === 1) cursor.varInt()
            transactions.set(tx.txid, tx.outputs)
        }
        return transactions
    } catch {
        return null
    }
}

/** What the ordinal's inputs are worth, from the source transactions in its BEEF. A source
 * counts only if it hashes to the input's txid, so the ordinal can't overstate it. Null when
 * any input's value is unknown. */
export function inputSatoshis(inputs: ActionInput[], inputBEEF: string | undefined): number | null {
    if (!inputs.length || !inputBEEF) return null
    const transactions = beefTransactions(inputBEEF)
    if (!transactions) return null
    let total = 0
    for (const input of inputs) {
        const satoshis = transactions.get(input.txid)?.[input.vout]
        if (!Number.isSafeInteger(satoshis)) return null
        total += satoshis as number
    }
    return total
}

/** Indexes of the outputs that pay the viewer's own address. */
export function outputsToAddress(outputs: ActionOutput[], address: string | null): Set<number> {
    const own = new Set<number>()
    if (!address) return own
    let script: string
    try {
        script = new P2PKH().lock(address).toHex()
    } catch {
        return own
    }
    outputs.forEach((output, index) => {
        if (output.lockingScript.toLowerCase() === script) own.add(index)
    })
    return own
}
